// Guard against DOM rewriting by Chrome/Google page translation (and similar
// browser extensions). Translation replaces React-owned text nodes with
// <font> wrappers, so React's later removeChild/insertBefore calls on those
// nodes throw NotFoundError — and with React 18 an uncaught commit error
// unmounts the whole tree, leaving a blank white page. Observed in the field:
// navigating LOCALE list -> "Edit Hotspots" on a translated tab
// ("EduVenture 作曲家") white-screened the app.
// Mitigation from https://github.com/facebook/react/issues/11538#issuecomment-417504600
// Import this module once, before ReactDOM renders.
(function installDomTranslationGuard() {
  if (typeof Node !== 'function' || !Node.prototype) {
    return; // No DOM in this environment — nothing to guard.
  }
  if (typeof window !== 'undefined') {
    if (window.__EV_DOM_TRANSLATION_GUARD_INSTALLED__) {
      return; // Idempotent: dev hot-reloads must not stack wrappers.
    }
    window.__EV_DOM_TRANSLATION_GUARD_INSTALLED__ = true;
  }

  // A translated page triggers this for dozens of nodes per navigation; log
  // enough to diagnose, then go quiet instead of flooding the console.
  const WARN_LIMIT = 10;
  let warnCount = 0;
  function warnIgnoredMutation(operation, node, parent) {
    if (typeof console === 'undefined' || !console.warn) {
      return;
    }
    warnCount += 1;
    if (warnCount > WARN_LIMIT) {
      return;
    }
    console.warn(
      '[domTranslationGuard] Ignored ' + operation + ' for a node that is no longer where React left it. ' +
      'This usually means the browser translated the page (e.g. Chrome "Translate this page") or an extension rewrote the DOM. ' +
      'The app keeps running, but translated text may become stale.',
      node, parent
    );
    if (warnCount === WARN_LIMIT) {
      console.warn('[domTranslationGuard] Further warnings of this kind will be suppressed.');
    }
  }

  // Realm-safe "is this a DOM node" check: `instanceof Node` is false for
  // nodes created in another document/iframe, so test the shape instead.
  function isNodeLike(value) {
    return Boolean(value) && typeof value === 'object' && typeof value.nodeType === 'number';
  }

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    // Invalid calls (null/non-Node child, non-Node `this`) are delegated
    // untouched so the browser's own TypeError / "Illegal invocation"
    // messages surface instead of a misleading guard message.
    if (!isNodeLike(this) || !isNodeLike(child)) {
      return originalRemoveChild.apply(this, arguments);
    }
    if (child.parentNode !== this) {
      // Covers both "moved into a <font> wrapper" and "already detached".
      warnIgnoredMutation('removeChild', child, this);
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    // referenceNode === null/undefined is a valid "append" call and missing
    // or malformed arguments must keep their native error messages, so only
    // node-like arguments are inspected here.
    if (!isNodeLike(this) || !isNodeLike(newNode) || (referenceNode != null && !isNodeLike(referenceNode))) {
      return originalInsertBefore.apply(this, arguments);
    }
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was hijacked, so any position we could pick might
      // be wrong; deliberately skip the insert (canonical mitigation). Worst
      // case some text is missing until the subtree re-renders — still far
      // better than unmounting the whole app.
      warnIgnoredMutation('insertBefore', referenceNode, this);
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
})();
